/**
 * Makes a stored product image URL fetchable, without changing which object it
 * points at.
 *
 * Image filenames come from the brand's own upload and routinely contain
 * characters that are not legal in a URL path. The upload path concatenated the
 * filename straight into the URL, so what is stored is frequently not a valid
 * URL at all:
 *
 *   .../803-Natural Linen-1.png     <- literal space
 *   .../DORA Black (2).jpg          <- space, from a real customer catalog
 *   .../100% Cotton.jpg             <- literal percent, and a space
 *   .../50%off.jpg                  <- literal percent, no space at all
 *
 * The browser silently rescues the first two: the URL parser percent-encodes a
 * space before requesting. It does NOT rescue a stray percent — `%of` is read as
 * a malformed escape, the request goes out malformed, and S3 answers 400. So the
 * tile renders as a broken image on the public line sheet and everywhere else
 * these cards appear, while the same product looks fine in the outreach email,
 * which repairs the URL server-side.
 *
 * This closes that gap at the point of render, which means it covers every row
 * already stored — no migration, and nothing to re-run the next time a brand
 * uploads "100% Cotton.jpg".
 *
 * The rules mirror the server's exactly (plg-go-be `linesheet/url_normalize.go`),
 * so the page and the email cannot disagree about what a URL means:
 *
 *   - a percent that does not begin a valid escape becomes %25
 *   - a space becomes %20
 *   - tab, CR and LF are dropped, as the URL parser drops them
 *   - anything outside printable ASCII is percent-encoded as UTF-8, one
 *     contiguous run at a time so a surrogate pair survives intact
 *
 * The last one is the only rule a browser would have applied for us anyway, and
 * it is here so the string we produce is a valid URL on its own rather than one
 * that happens to survive Chrome's leniency — the same bytes the server emits.
 *
 * A percent that ALREADY begins an escape is left alone, so a correctly stored
 * URL is untouched and repeated passes are stable — %20 never becomes %2520.
 *
 * Only absolute http(s) URLs are touched. Anything else — a local asset path, a
 * data: placeholder, an empty value — is returned as-is for its own code to
 * handle.
 */
export function normalizeImageUrl(url) {
  if (typeof url !== 'string' || !/^https?:\/\//i.test(url)) {
    return url
  }

  return url
    .replace(/%(?![0-9a-fA-F]{2})/g, '%25')
    .replace(/ /g, '%20')
    .replace(/[\t\r\n]/g, '')
    .replace(/[^ -~]+/gu, sequence => encodeURIComponent(sequence))
}

export default normalizeImageUrl
